import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import TextareaAutosize from 'react-textarea-autosize';
import { useForm } from 'react-hook-form';
import ReactModal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useSystemMessage } from '../../../hooks/useSystemMessage';
import { taskcommentApi } from '../../../apis/taskcomment/taskcommentApi';
import icon05 from '../../../assets/images/ROOM_TaskFile_Re_05.png';
import { TaskComment } from '../../../types/types';
import {ApiClientError} from "../../../apis/errors";
import {ValidationErrorMessages} from "../common/ValidationErrorMessages";
import {logError} from "../../../libs/utils/logError";
import useAppRouter from "../../../hooks/useAppRouter";

interface UpdateTaskCommentModalProps {
  taskComment: TaskComment;
  onSubmitted: () => Promise<void>;
}

interface FormValues {
  content: string;
}

export const UpdateTaskCommentModal = (props: UpdateTaskCommentModalProps) => {
  const { taskComment, onSubmitted } = props;
  const { success } = useSystemMessage();
  const location = useLocation();
  const { goto } = useAppRouter();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      content: taskComment.content,
    },
    reValidateMode: 'onSubmit',
  });

  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);

  const onSubmit = async (data: FormValues) => {
    setApiClientError(null);
    try {
      await taskcommentApi.update(
        taskComment.id,
        { taskCommentContent: data.content },
        {}
      );
      await success('コメントを更新しました');
      await onSubmitted();
    } catch(e) {
      await logError(
        location.pathname,
        'コメントの更新に失敗しました。',
        e
      );

      if (e instanceof ApiClientError) {
        setApiClientError(e);
        return;
      }

      goto('internalServerError');
    }
  };

  return (
    <>
      <ReactModal
        isOpen
        ariaHideApp={false}
        onRequestClose={() => onSubmitted()}
        className="content"
        overlayClassName="overlay"
        shouldCloseOnOverlayClick
      >
        <div className="form-modal-container">
          <div className="form-modal-title">
            <h1 className="font-semibold">コメント編集</h1>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => onSubmitted()}
            >
              <img src={icon05} alt="icon" />
            </button>
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="form-modal-content">
              <div className="text-danger text-xs mb-4">
                { apiClientError?.message }
                { (Object.keys(errors).length > 0) && '入力エラーがあります' }
              </div>
              <TextareaAutosize
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...register('content', { required: true, maxLength: 10000 })}
                minRows={7}
                maxRows={7}
                placeholder="コメントを入力してください"
                className="text-area"
              />
              {errors.content && errors.content.type === 'required' && (
                <span className="my-2 text-red-500 text-xs">
                  コメント内容は必須です
                </span>
              )}
              {errors.content && errors.content.type === 'maxLength' && (
                <span className="my-2 text-red-500 text-xs">
                  コメント内容は10,000文字以下で指定してください
                </span>
              )}
              <ValidationErrorMessages
                {...{
                  fieldErrors: apiClientError?.errors,
                  field: 'taskCommentContent',
                }}
              />
            </div>
            <div className="form-modal-footer-container">
              <div className="form-modal-footer">
                <button
                  className="btn-sub"
                  type="button"
                  onClick={() => onSubmitted()}
                >
                  キャンセル
                </button>
                <button
                  className="btn-primary"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <FontAwesomeIcon
                        icon={faSpinner}
                        className="animate-spin mr-4"
                      />
                      保 存
                    </>
                  ) : (
                    '保　存'
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </ReactModal>
    </>
  );
};
